import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../store";
import MainNavigation from "../../components/navigation/MainNavigation";
import SideNavigation from "../../components/navigation/SideNavigation";
import Story from "../../components/story/Story";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { fetchFeedStreamItems } from "../../store/slices/feed";
import {
  fetchActiveFeedItem,
  fetchActiveFeedItemCVI,
  fetchActiveFeedItemSegmentCVI,
  fetchActiveFeedItemNumbers,
} from "../../store/slices/activeFeedItem";
import { CircleLoader } from "../../icons/Loaders";
import DataNotAvailable from "../../components/errors/DataNotAvailable";
import { parseSectionFiltersFromSearchParams } from "../../helpers/sectionFilters";
import { v4 as uuidv4 } from "uuid";

const Stream = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const { id } = useParams();

  const { streamData: items, isLoading } = useSelector((state: any) => state.feed);
  const selectedSegment = useSelector((state: any) => state.activeFeedItem.selectedSegment);
  const { dashboardSettings } = useSelector((state: any) => state.currentBrand.data);

  const [streamItemsFetchedOnInitialLoad, setStreamItemsFetchedOnInitialLoad]: [boolean, Function] = useState(false);
  const [selectedFilter, setSelectedFilter]: [string, Function] = useState("");
  const [selectedSections, setSelectedSections]: [string[], Function] = useState([]);
  const [storyType, setStoryType]: [string, Function] = useState("story");

  const filter = searchParams.get("filter");
  const formattedSections = parseSectionFiltersFromSearchParams(
    searchParams.get("sectionFilters"),
  );

  useEffect(() => {
    if (!isLoading && items?.length === 0) {
      document.title = "Stream";
      document.body.dataset.titleReady = "true";
    }
  },[isLoading, items])

  // Initial fetching of stream data
  useEffect(() => {
    if (!streamItemsFetchedOnInitialLoad) {
      setSelectedFilter(filter || "");
      setSelectedSections(formattedSections);
      dispatch(fetchFeedStreamItems(formattedSections));
      setStreamItemsFetchedOnInitialLoad(true);
    }
  }, [dispatch, filter, formattedSections, streamItemsFetchedOnInitialLoad]);

  // Interval refetching of stream data
  useEffect(() => {
    const interval = setInterval(() => {
      dispatch(fetchFeedStreamItems(formattedSections));
    }, dashboardSettings?.storyListRefreshTime || 60000);

    return () => clearInterval(interval);
  }, [dispatch, dashboardSettings?.storyListRefreshTime, formattedSections]);

  const transformedItems = useMemo(() => {
    return items.map((e: any) => {
      return {
        title: e.title,
        description: e.content,
        itemType: e.item_type,
        itemData: e.item_data,
        postId: e.post_id,
        itemId: e.item_id,
        postType: e.post_type,
        inked: e.inked,
        pubDate: e.pubdate,
        key: uuidv4(),
      };
    });
  }, [items]);

  useEffect(() => {
    if (!id && transformedItems[0]?.postId) {
      navigate(`/stream/${transformedItems[0].postId}`);
    }
  }, [transformedItems, id, navigate]);

  useEffect(() => {
    if (!id) return;

    if (id && transformedItems.length > 0) {
      if (transformedItems.find((e) => e.postId === id)?.postType !== "video") {
        setStoryType("story");
        dispatch(fetchActiveFeedItem(id, {}));
        if (selectedSegment.key !== "" && selectedSegment.key !== "No segments selected") {
          dispatch(fetchActiveFeedItemSegmentCVI(id, selectedSegment.key));
        } else {
          dispatch(fetchActiveFeedItemCVI(id));
        }
        dispatch(fetchActiveFeedItemNumbers(id));
      } else {
        setStoryType("videos");
        dispatch(
          fetchActiveFeedItem(id, {
            primaryStoryType: "videos",
          }),
        );
        dispatch(fetchActiveFeedItemCVI(id, "videos"));
        dispatch(fetchActiveFeedItemNumbers(id, { primaryStoryType: "videos" }));
      }
    }
  }, [id, dispatch, selectedSegment.key]);

  const filters = [
    {
      filter: "Acts",
      selected: false,
    },
    {
      filter: "Updates",
      selected: false,
    },
    {
      filter: "Comments",
      selected: false,
    },
    {
      filter: "Tentacles",
      selected: false,
    },
  ];

  const pageContent = useMemo(() => {
    if (isLoading) {
      return (
        <div className="loader-container">
          <CircleLoader />
        </div>
      );
    }
    if (!isLoading && transformedItems.length === 0) {
      return <DataNotAvailable pageType="stream" />;
    }
    if (storyType === "videos") {
      return <Story type="videos" />;
    }
    return <Story />;
  }, [isLoading, transformedItems.length, storyType]);

  return (
    <div>
      <MainNavigation />
      <div className="container">
        <SideNavigation
          title="Stream"
          items={transformedItems}
          filters={filters}
          isLoading={isLoading}
          fetchFunctionForSections={fetchFeedStreamItems}
          hasSectionFilter
          filtersMessage={"Which stream items do you want to see?"}
          selectedFilter={selectedFilter}
          setSelectedFilter={setSelectedFilter}
          selectedSections={selectedSections}
          setSelectedSections={setSelectedSections}
        />
        <div className="main-page-container">{pageContent}</div>
      </div>
    </div>
  );
};

export default Stream;
